import { IWorkerSummary } from 'types/INodeAgent';
import { agentVersionAtLeast, splitSoftwareRef } from 'utils/softwareRef';

// Mirrors backend/tailor/service/node_agent_service.py: the first pytailor whose node
// agent understands `replace` on start_worker. An older agent ignores the key and adds a
// worker beside the old one, so the backend refuses the command; the dialogs disable it.
export const MIN_REPLACE_PYTAILOR_VERSION = '0.33.0';

export function agentSupportsReplace(pytailorVersion: string | null | undefined): boolean {
  return agentVersionAtLeast(pytailorVersion, MIN_REPLACE_PYTAILOR_VERSION);
}

// PEP 503, as the worker's software advert and the agent's group key apply it.
export function normalizeSoftwareName(name: string): string {
  return name.replace(/[-_.]+/g, '-').toLowerCase();
}

export interface GroupParams {
  name: string;
  projectIdFilter?: string[] | null;
  capabilities?: string[] | null;
  packageName?: string | null;
  softwareRef?: string | null;
}

// The software a worker runs, by name only: the package (pin stripped) or the Depot
// release the ref names. Empty falls back to the agent's own default package.
export function softwareNameOf(params: Pick<GroupParams, 'packageName' | 'softwareRef'>): string {
  const pkg = (params.packageName ?? '').split('==')[0].trim();
  if (pkg) return normalizeSoftwareName(pkg);
  const ref = (params.softwareRef ?? '').trim();
  if (ref) return normalizeSoftwareName(splitSoftwareRef(ref).name);
  return 'pytailor';
}

// What work a worker serves — the agent's group key: name, project filter, capabilities
// and the software's normalised name. Version, source, execution mode, the batch flag and
// tuning knobs are deliberately not part of it.
export function groupKey(params: GroupParams): string {
  return JSON.stringify([
    // Raw, as ManagedWorker.group_key holds worker_name and StartWorkerDialog sends it:
    // trimming here would match `w ` to group `w`, offer Replace, and then send a command
    // the agent reads as a different group — a replacement that replaces nothing.
    params.name,
    [...(params.projectIdFilter ?? [])].sort(),
    [...(params.capabilities ?? [])].sort(),
    softwareNameOf(params),
  ]);
}

export function groupParamsOf(worker: IWorkerSummary): GroupParams {
  return {
    name: worker.name,
    projectIdFilter: worker.project_id_filter,
    capabilities: worker.capabilities,
    packageName: worker.package_name,
    softwareRef: worker.software ?? worker.software_ref,
  };
}

// The node's workers that share a group with `params` — what `replace` would replace.
export function groupMembers(workers: IWorkerSummary[], params: GroupParams): IWorkerSummary[] {
  const key = groupKey(params);
  return workers.filter((ws) => ws.instance_id && groupKey(groupParamsOf(ws)) === key);
}

// The budget a replacement defaults to: the sum over the group's *active* members —
// those not already being replaced by another member. A retiring member's budget is
// vacated into its replacer's, so counting it again would grow the group on every roll.
export function defaultReplacementBudget(members: IWorkerSummary[]): number {
  const retiring = new Set(members.flatMap((ws) => ws.replacing ?? []));
  return members
    .filter((ws) => ws.instance_id && !retiring.has(ws.instance_id))
    .reduce((sum, ws) => sum + (ws.budget ?? ws.slots ?? 0), 0);
}
